/**
 * Static program and coach lists, mirrored from `back-end/src/domains/content/repo.ts`.
 *
 * The home, programs, and instructors pages used to fetch this from
 * `/api/content/programs` and `/api/content/instructors`. The data is purely static
 * (constants in the back-end repo, no DB, no per-request logic), so a network round-trip
 * on first paint just delayed the cards rendering — and on a cold back-end dyno or under
 * connection-pool pressure from the gallery carousel the cards could fail to appear at
 * all until the request finally resolved. Inlining lets the cards render synchronously.
 *
 * Keep in sync with the back-end repo (single source of truth for any code that actually
 * runs server-side, e.g. emails or future admin views).
 */

export type ContentProgram = { slug: string; title: string; blurb: string; bullets: string[] };
export type ContentInstructor = { slug: string; name: string; title: string; bio: string };

export const PROGRAMS: ContentProgram[] = [
  {
    slug: 'boxing',
    title: 'Boxing',
    blurb: 'Structured progression from Beginners I and II through Intermediate and Advanced.',
    bullets: [
      'Footwork, punching mechanics, combinations, focus mitts',
      'Bag work, conditioning, sparring when ready',
      'USA Boxing certified coaching',
    ],
  },
  {
    slug: 'muay-thai',
    title: 'Muay Thai',
    blurb: 'The art of eight weapons: fists, elbows, knees, shins, feet.',
    bullets: [
      'Beginners (Fundamentals): 24 classes, about 2 months',
      'Intermediate: 32 classes intermediate plus 8 punching fundamentals',
      'Advanced: training for competition begins here',
    ],
  },
  {
    slug: 'mma',
    title: 'MMA',
    blurb:
      'Stand-up striking for MMA students and fighters, built on Boxing and Muay Thai fundamentals.',
    bullets: [
      'MMA Intermediate (Mon and Wed evenings)',
      'MMA Fighters (Tue and Thu evenings)',
      'MMA Beginners (Saturday afternoon)',
    ],
  },
  {
    slug: 'bjj',
    title: 'Brazilian Jiu-Jitsu',
    blurb:
      'Grappling and ground fighting. A smaller, weaker person can defend against a bigger, stronger assailant through leverage, joint locks, and chokeholds.',
    bullets: [
      'Fundamental defenses, escapes, and submissions from every position',
      'Gi classes Monday through Thursday',
      'Counters to each technique covered',
    ],
  },
  {
    slug: 'kids',
    title: 'Kids Programs',
    blurb: 'Boxing, Wrestling, and Brazilian Jiu-Jitsu for ages 8 - 13.',
    bullets: [
      'Kids Boxing: fundamentals plus fitness',
      'Kids Jiu Jitsu: basic grappling and self-defense',
      'Kids Wrestling: folkstyle and greco takedowns',
    ],
  },
];

export const INSTRUCTORS: ContentInstructor[] = [
  {
    slug: 'george-prevalsky',
    name: 'George Prevalsky',
    title: 'Head Instructor',
    bio: 'IAMTF Master of Muay Thai (Ajahn) since 1998 and USA Boxing registered coach. 30+ years of martial arts experience across Western Boxing, Kickboxing, and Muay Thai.',
  },
  {
    slug: 'kevin-kildea',
    name: 'Kevin Kildea',
    title: 'Certified USA Boxing Instructor',
    bio: 'USA Boxing certified. 30+ years across Karate, Boxing, Kickboxing, Jeet Kune Do, Hapkido, Jiujitsu, Escrima, and Muay Thai. Competitive marathoner.',
  },
  {
    slug: 'victor-ling',
    name: 'Victor Ling',
    title: 'Assistant Muay Thai Instructor',
    bio: "Muay Thai fighter; assists with the gym's Muay Thai instruction.",
  },
  {
    slug: 'yousef-alirezaei',
    name: 'Yousef Alirezaei',
    title: 'Brazilian Jiu-Jitsu Instructor',
    bio: '3rd degree BJJ black belt. 30 years of wrestling and BJJ experience; 250+ gold medals. 2x SAMBO World Champion (2001, 2002).',
  },
];
